import React, { useCallback } from 'react';
import { HBox, VBox } from '../../layout';
import { Icon, NameCircle } from '../../basic';
import { isToday } from 'date-fns';
import { DefaultDateFormatter, DefaultTimeFormatter, intl } from '../../../lib';
import { ChatUser } from '../chatTypes';
import { ChatOptions } from './ChatMessageComponentsTypes';
import DOMPurify from 'dompurify';

/**
 * Props for a {@link ChatTextMessage} component.
 * @author Zuzana Stichova, Tomas Lukac
 */
export type ChatMessageProps = {
  /**
   * Person sending the message.
   */
  person: Omit<ChatUser, 'uid'>;
  /**
   * Date when message was sent. You can specify string for your date formatting, otherwise default will be used which renders time for today and date for older messages.
   */
  date: Date | string;
  /**
   * Text of the message.
   */
  text: string;
  /**
   * Text of the error.
   */
  errorText?: string;
} & Pick<ChatOptions, 'headerOptions'>;

export default function ChatTextMessage({
  person,
  date,
  text,
  errorText,
  headerOptions = {
    isIconVisible: true,
    isDateVisible: true,
    isNameVisible: true
  }
}: ChatMessageProps): JSX.Element {
  const { firstname, lastname, isSender } = person;

  const formatDate = useCallback((date: string | Date): string => {
    if (typeof date === 'string') {
      return date;
    }
    return isToday(date)
      ? DefaultTimeFormatter.formatDate(date)
      : DefaultDateFormatter.formatDate(date);
  }, []);

  return (
    <HBox className="ehs-chat-text-component" reverse={isSender}>
      <NameCircle
        firstName={firstname}
        lastName={lastname}
        className="name-circle"
      />
      <VBox className="text-message">
        <div className={`text-message-body ${isSender ? 'sender' : ''}`}>
          <HBox className="text-message-header" wrap="wrap">
            {headerOptions.isNameVisible && (
              <div>
                {isSender ? intl('common.you') : `${firstname} ${lastname}`}
              </div>
            )}
            {headerOptions.isIconVisible && (
              <Icon
                iconName="dot"
                aria-label={undefined}
                className="text-message-dot-icon"
              />
            )}
            {headerOptions.isDateVisible && (
              <div>
                <time dateTime={formatDate(date)}>{formatDate(date)}</time>
              </div>
            )}
          </HBox>
          <p className="chat-message-text">{DOMPurify.sanitize(text)}</p>
        </div>
        {errorText && (
          <HBox className="chat-message-error">
            <Icon
              iconName="materialicon-error"
              aria-label={intl('common.error')}
            />
            <p>{errorText}</p>
          </HBox>
        )}
      </VBox>
    </HBox>
  );
}
